import { WebStorageStateStore } from "oidc-client-ts";
import { createContext, type ReactNode, useContext } from "react";
import { AuthProvider, useAuth } from "react-oidc-context";
import { authority, config } from "./config";
import { looksLikeUserId } from "./lib/people";
import { returnPath, signInState } from "./lib/return-to";

export interface SessionInfo {
  ready: boolean;
  authenticated: boolean;
  name: string;
  email: string;
  token: string | undefined;
  signIn: () => void;
  signOut: () => void;
}

const devSession: SessionInfo = {
  ready: true,
  authenticated: true,
  name: "Dev",
  email: "dev@localhost",
  token: undefined,
  signIn: () => undefined,
  signOut: () => undefined,
};

const SessionContext = createContext<SessionInfo>(devSession);

/**
 * Cognito hosted UI (managed login) with the OIDC authorization-code + PKCE
 * flow. When no Cognito client is configured (local development) the app
 * runs unauthenticated against an API started with SKYNET_AUTH_DISABLED=1.
 */
export function SkynetAuthProvider({ children }: { children: ReactNode }) {
  if (config.authDisabled)
    return <SessionContext.Provider value={devSession}>{children}</SessionContext.Provider>;
  return (
    <AuthProvider
      authority={authority}
      client_id={config.cognito.clientId}
      redirect_uri={`${window.location.origin}/auth/callback`}
      post_logout_redirect_uri={`${window.location.origin}/`}
      response_type="code"
      scope="openid email profile"
      automaticSilentRenew
      userStore={new WebStorageStateStore({ store: window.localStorage })}
      metadata={{
        issuer: authority,
        authorization_endpoint: `${config.cognito.domain}/oauth2/authorize`,
        token_endpoint: `${config.cognito.domain}/oauth2/token`,
        userinfo_endpoint: `${config.cognito.domain}/oauth2/userInfo`,
        end_session_endpoint: `${config.cognito.domain}/logout`,
        jwks_uri: `${authority}/.well-known/jwks.json`,
      }}
      onSigninCallback={(user) => {
        window.history.replaceState({}, document.title, returnPath(user?.state));
      }}
    >
      <CognitoSessionBridge>{children}</CognitoSessionBridge>
    </AuthProvider>
  );
}

function profileName(profile: Record<string, unknown> | undefined): string {
  const name = typeof profile?.name === "string" ? profile.name.trim() : "";
  if (name && !looksLikeUserId(name)) return name;
  const given = typeof profile?.given_name === "string" ? profile.given_name.trim() : "";
  const family = typeof profile?.family_name === "string" ? profile.family_name.trim() : "";
  const combined = `${given} ${family}`.trim();
  return looksLikeUserId(combined) ? "" : combined;
}

function CognitoSessionBridge({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const token = auth.user?.access_token;
  const idToken = auth.user?.id_token;
  // TanStack Query starts fetches inside useSyncExternalStore, during render,
  // which is before any useEffect. Register the token here so those requests
  // already have a bearer token.
  registerTokenGetter(() => token);
  registerIdTokenGetter(() => idToken);
  const profile = auth.user?.profile as Record<string, unknown> | undefined;
  const email =
    (typeof profile?.email === "string" ? profile.email : undefined) ?? auth.user?.profile.sub ?? "";
  const value: SessionInfo = {
    ready: !auth.isLoading,
    authenticated: auth.isAuthenticated,
    name: profileName(profile),
    email,
    token,
    signIn: () => void auth.signinRedirect({ state: signInState(window.location) }),
    signOut: () => {
      const logout = new URL(`${config.cognito.domain}/logout`);
      logout.searchParams.set("client_id", config.cognito.clientId);
      logout.searchParams.set("logout_uri", `${window.location.origin}/`);
      void auth.removeUser().then(() => {
        window.location.href = logout.toString();
      });
    },
  };
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionInfo {
  return useContext(SessionContext);
}

let tokenGetter: () => string | undefined = () => undefined;
let idTokenGetter: () => string | undefined = () => undefined;

/** Lets the API client read the current access token without React context. */
export function registerTokenGetter(getter: () => string | undefined): void {
  tokenGetter = getter;
}

export function registerIdTokenGetter(getter: () => string | undefined): void {
  idTokenGetter = getter;
}

export function currentToken(): string | undefined {
  return tokenGetter();
}

/** ID token carries name and email, which the access token usually omits. */
export function currentIdToken(): string | undefined {
  return idTokenGetter();
}
